/**
 * Phase 4.4 — lightweight publish-success confetti.
 *
 * Standalone burst that doesn't pull in `canvas-confetti` (~6KB but adds
 * a new dep). We render ~36 colored squares onto a canvas pinned over the
 * viewport, animate them with simple physics, then drop the canvas when
 * everything settles.
 *
 * Callers must check `prefers-reduced-motion` themselves — this util always
 * animates when invoked.
 */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rot: number;
  rotSpeed: number;
  color: string;
  life: number;
}

const COLORS = [
  "#60871b", // brand green
  "#8bc129", // brand light
  "#f59e0b", // amber accent
  "#3b82f6", // info blue
  "#a855f7", // celebratory violet
  "#ffffff", // sparkle highlight
];

const PARTICLE_COUNT = 36;
const GRAVITY = 0.18;
const DRAG = 0.992;
const LIFESPAN_MS = 1600;

/**
 * Fire a confetti burst from the top-center of the viewport. Cleans up its
 * own canvas/timer when the animation completes.
 */
export function triggerPublishConfetti(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  canvas.style.position = "fixed";
  canvas.style.inset = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "2000";

  const dpr = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * dpr;
  canvas.height = height * dpr;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(dpr, dpr);

  document.body.appendChild(canvas);

  const originX = width / 2;
  const originY = height * 0.15;

  const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, () => {
    const angle = Math.random() * Math.PI - Math.PI / 2; // upward spread
    const speed = 6 + Math.random() * 6;
    return {
      x: originX,
      y: originY,
      vx: Math.cos(angle) * speed * (Math.random() < 0.5 ? -1 : 1),
      vy: Math.sin(angle) * speed - 4,
      size: 5 + Math.random() * 5,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? "#ffffff",
      life: 1,
    };
  });

  const start = performance.now();
  let raf = 0;

  const tick = (now: number) => {
    const elapsed = now - start;
    const t = Math.min(elapsed / LIFESPAN_MS, 1);
    ctx.clearRect(0, 0, width, height);

    for (const p of particles) {
      p.vy += GRAVITY;
      p.vx *= DRAG;
      p.vy *= DRAG;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.rotSpeed;
      p.life = 1 - t;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(p.life, 0);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
      ctx.restore();
    }

    if (t < 1) {
      raf = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(raf);
      canvas.remove();
    }
  };

  raf = requestAnimationFrame(tick);
}
